import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Box,
  Flex,
  Link,
  type SegmentedControlOption,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { LossRateCard } from '~/pages/AdminLossRate/components/LossRateCard'
import { useListLossRateSettings } from '~/pages/AdminLossRate/hooks/useListLossRateSettings'
import { convertListResponse } from '~/pages/AdminLossRate/models/converter'
import { deriveVisibleAreaCodes } from '~/pages/AdminLossRate/models/visibleAreas'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { routePaths } from '~/routes/routePaths'

/**
 * 表示するエリアの範囲 (design/admin/loss-rate.html `#lr-area-scope`)。
 * 既定は `business` (= 個人設定の業務エリア)。
 */
type AreaScopeKey = 'business' | 'all'

/** design segmented の表示順 (loss-rate.html の `.ecl-segmented-item` 2 個)。 */
const AREA_SCOPES: readonly AreaScopeKey[] = ['business', 'all']

const AREA_SCOPE_I18N_KEYS: Record<AreaScopeKey, string> = {
  business: 'page:adminLossRate.toolbar.areaScope.business',
  all: 'page:adminLossRate.toolbar.areaScope.all',
}

export const AdminLossRatePage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [areaScope, setAreaScope] = useState<AreaScopeKey>('business')

  const areaScopeOptions: Array<SegmentedControlOption<AreaScopeKey>> =
    AREA_SCOPES.map((scope) => ({
      value: scope,
      label: t(AREA_SCOPE_I18N_KEYS[scope]),
    }))

  const { data: listResponse, isLoading: isListLoading } =
    useListLossRateSettings()
  const { data: personalSettings, isLoading: isSettingsLoading } =
    useGetPersonalSettingsQuery()

  const settings = useMemo(
    () => (listResponse !== undefined ? convertListResponse(listResponse) : []),
    [listResponse],
  )

  // §8 と同様の方針 (Supply48Data): 個人設定ロード完了まで area fallback しない
  // (= UNSPECIFIED のまま扱わず、単に「まだ分からない」として空表示にする)。
  const businessAreas = useMemo(
    () => personalSettings?.settings?.businessAreas ?? [],
    [personalSettings],
  )

  // 業務エリア順を保持しつつ表示対象を決める (design lrRender と同じ方針)。
  const visibleAreas =
    areaScope === 'all'
      ? AREA_CODE_OPTIONS
      : deriveVisibleAreaCodes(businessAreas)

  const isLoading = isListLoading || isSettingsLoading

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminLossRate.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        { label: t('common:route.adminLossRate'), bold: true },
      ]}
    >
      <PageBody>
        <PageScrollArea>
          <Flex flexDirection="column" gap={Spacing[4].value}>
            {/* design `.page-description` = md / `--text-high-emphasis` / margin 0 /
                line-height 1.6 (8080 実測)。 色は primary 固定で mid にしない
                (2026-08-06 デザイナー指摘「primary 色にして、もっと視認性上げたい」)。 */}
            <Text
              as="p"
              color="Text.highEmphasis"
              fontSize="md"
              style={{ margin: 0, lineHeight: 1.6 }}
            >
              {t('page:adminLossRate.description')}{' '}
              {/* DS `Link` (色 Text.accentPrimary / hover で下線 + offset / focus ring)
                  を使い、遷移は RouterLink に委ねる (asChild)。
                  design `.ecl-text-link` も同値で、hover 時に下線が出るのが正。
                  既存の同型実装: AdminWeatherForecastPoints/index.tsx。 */}
              <Link asChild>
                <RouterLink to={routePaths.settings.path}>
                  {t('page:adminLossRate.descriptionLinkLabel')}
                </RouterLink>
              </Link>
              {t('page:adminLossRate.descriptionSuffix')}
            </Text>

            <Flex
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={Spacing[3].value}
            >
              <Text textStyle="sectionTitle">
                {t('page:adminLossRate.toolbar.title')}
              </Text>
              {/* design は 2 択の segmented (業務エリア / 全エリア) で現在の表示範囲を
                  常に見せる。 DS `SegmentedControl` そのままでは radio がキーボードからも
                  a11y tree からも失われる (= `display: none`) ため、 補正込みの共通ラッパを使う
                  (理由は AccessibleSegmentedControl の JSDoc 参照)。 */}
              <AccessibleSegmentedControl
                aria-label={t('page:adminLossRate.toolbar.areaScopeGroupLabel')}
                name="admin-loss-rate-area-scope"
                onSelect={setAreaScope}
                options={areaScopeOptions}
                selected={areaScope}
              />
            </Flex>

            <RegionLoading isLoading={isLoading}>
              {isLoading ? null : visibleAreas.length === 0 ? (
                <Box
                  color="Text.midEmphasis"
                  fontSize="md"
                  textAlign="center"
                  paddingX={Spacing[3].value}
                  paddingY={Spacing[8].value}
                >
                  <Text as="span">{t('page:adminLossRate.empty.prefix')}</Text>{' '}
                  <Link asChild>
                    <RouterLink to={routePaths.settings.path}>
                      {t('page:adminLossRate.empty.linkLabel')}
                    </RouterLink>
                  </Link>
                  <Text as="span">{t('page:adminLossRate.empty.suffix')}</Text>
                </Box>
              ) : (
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(360px, 1fr))',
                    gap: Spacing[3].value,
                  }}
                >
                  {visibleAreas.map((areaCode) => {
                    const setting = settings.find(
                      (s) => s.areaCode === areaCode,
                    )
                    if (setting === undefined) {
                      return null
                    }
                    return (
                      <LossRateCard
                        key={areaCode}
                        appliedDate={setting.currentEffectiveDate}
                        areaLabel={convertAreaLabel(areaCode)}
                        next={
                          setting.next !== undefined
                            ? {
                                effectiveDate: setting.next.effectiveDate,
                                values: setting.next.values,
                              }
                            : undefined
                        }
                        values={setting.current}
                      />
                    )
                  })}
                </Box>
              )}
            </RegionLoading>
          </Flex>
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
